import React, { useMemo, useState } from 'react';
import { DataTable } from '../components/DataTable';
import { LoadingBlock } from '../components/LoadingBlock';
import { SectionCard } from '../components/SectionCard';

const EMPTY_FORM = {
  name: '',
  short_name: '',
  city: '',
  state: '',
  logo_url: '',
  primary_color: '#0E7A3E',
  secondary_color: '#D4A017',
  owner_user_id: '',
  manager_user_id: '',
  coach_user_id: '',
  assistant_user_id: '',
  analyst_user_id: '',
  store_user_id: '',
  status: 'active',
};

export function TeamsPage({
  loading,
  error,
  data,
  onCreateTeam,
  onUpdateTeam,
  onTransferTeamResponsibility,
  onDeleteTeam,
  onUploadTeamLogo,
  canManage,
  canCreate,
  canOverrideResponsibility,
  creating,
  updating,
  transferringResponsibility,
  uploadingLogo,
  deleting,
}) {
  const [form, setForm] = useState(EMPTY_FORM);
  const [editingId, setEditingId] = useState(null);
  const [selectedLogoFile, setSelectedLogoFile] = useState(null);
  const [logoMessage, setLogoMessage] = useState('');
  const [responsibilityReason, setResponsibilityReason] = useState('');
  const isEditing = Boolean(editingId);
  const users = data?.users || [];

  const userOptions = useMemo(() => users.map((user) => ({
    value: String(user.id),
    label: `${user.name} (${(user.roles || []).join(', ') || 'sem perfil'})`,
  })), [users]);

  const resetForm = () => {
    setForm(EMPTY_FORM);
    setEditingId(null);
    setSelectedLogoFile(null);
    setLogoMessage('');
    setResponsibilityReason('');
  };

  const uploadSelectedLogo = async () => {
    if (!selectedLogoFile || !onUploadTeamLogo) {
      return;
    }

    const url = await onUploadTeamLogo(selectedLogoFile, editingId);
    if (url) {
      setForm((current) => ({ ...current, logo_url: url }));
      setSelectedLogoFile(null);
      setLogoMessage('Logo enviado. Salve o time para manter a alteração.');
    }
  };

  const removeCurrentLogo = () => {
    setSelectedLogoFile(null);
    setForm((current) => ({ ...current, logo_url: '' }));
    setLogoMessage('Logo removido. Salve o time para aplicar o fallback no app.');
  };

  if (loading) return <LoadingBlock label="Carregando times..." />;
  if (error) return <div className="page-error">{error}</div>;

  return (
    <div className="page-grid two-columns">
      <SectionCard
        title="Times"
        subtitle="Cadastre o clube, responsável e comissão técnica sem abrir edição para quem não deve."
        actions={canManage && isEditing ? <button className="ghost-button" type="button" onClick={resetForm}>Cancelar edição</button> : null}
      >
        {canManage && (isEditing || canCreate) ? (
          <form
            className="admin-form"
            onSubmit={(event) => {
              event.preventDefault();
              const payload = { ...form };
              Object.keys(payload).forEach((key) => {
                if (payload[key] === '') {
                  payload[key] = null;
                }
              });

              if (isEditing) {
                onUpdateTeam(editingId, payload).then?.(resetForm);
                return;
              }

              onCreateTeam(payload).then?.(resetForm);
            }}
          >
            <div className="callout-box compact-box">
              <span className="eyebrow">Acesso do administrador</span>
              <strong>Controle total do cadastro do time</strong>
              <p>Como dono ou admin, você pode adicionar, editar e excluir times, além de vincular responsável, comissão e loja.</p>
            </div>
            <div className="form-row">
              <label>
                Nome
                <input value={form.name} onChange={(event) => setForm({ ...form, name: event.target.value })} required />
              </label>
              <label>
                Sigla
                <input value={form.short_name} onChange={(event) => setForm({ ...form, short_name: event.target.value.toUpperCase() })} required />
              </label>
            </div>
            <div className="form-row triple">
              <label>
                Cidade
                <input value={form.city} onChange={(event) => setForm({ ...form, city: event.target.value })} />
              </label>
              <label>
                UF
                <input value={form.state} maxLength="2" onChange={(event) => setForm({ ...form, state: event.target.value.toUpperCase() })} />
              </label>
              <label>
                Status
                <select value={form.status} onChange={(event) => setForm({ ...form, status: event.target.value })}>
                  <option value="active">Ativo</option>
                  <option value="inactive">Inativo</option>
                </select>
              </label>
            </div>
            <div className="media-uploader">
              <div className="media-preview">
                {form.logo_url ? (
                  <img
                    src={form.logo_url}
                    alt={`Escudo de ${form.name || 'time'}`}
                    onError={(event) => {
                      event.currentTarget.style.display = 'none';
                    }}
                  />
                ) : (
                  <span>{(form.short_name || form.name || 'TIME').slice(0, 3).toUpperCase()}</span>
                )}
              </div>
              <div className="media-uploader__controls">
                <label>
                  Escudo/logo do time
                  <input
                    type="file"
                    accept="image/jpeg,image/png,image/webp"
                    onChange={(event) => setSelectedLogoFile(event.target.files?.[0] || null)}
                  />
                </label>
                <div className="inline-actions">
                  <button className="ghost-button" type="button" onClick={uploadSelectedLogo} disabled={!selectedLogoFile || uploadingLogo}>
                    {uploadingLogo ? 'Enviando...' : 'Enviar logo'}
                  </button>
                  <button className="inline-button" type="button" onClick={removeCurrentLogo} disabled={!form.logo_url && !selectedLogoFile}>
                    Remover logo
                  </button>
                </div>
                <small>JPG, PNG ou WEBP até 5 MB. O escudo aparece na Home, jogos e central quando disponível.</small>
                {logoMessage ? <small className="success-note compact">{logoMessage}</small> : null}
                <label>
                  Logo URL
                  <input value={form.logo_url} onChange={(event) => setForm({ ...form, logo_url: event.target.value })} />
                </label>
              </div>
            </div>
            <div className="form-row">
              <label>
                Cor principal
                <input type="color" value={form.primary_color} onChange={(event) => setForm({ ...form, primary_color: event.target.value })} />
              </label>
              <label>
                Cor secundária
                <input type="color" value={form.secondary_color} onChange={(event) => setForm({ ...form, secondary_color: event.target.value })} />
              </label>
            </div>
            <div className="form-row">
              <label>
                Responsável por time
                <select value={form.owner_user_id || ''} onChange={(event) => setForm({ ...form, owner_user_id: event.target.value })}>
                  <option value="">Não definir</option>
                  {userOptions.map((option) => <option key={option.value} value={option.value}>{option.label}</option>)}
                </select>
              </label>
              <label>
                Manager
                <select value={form.manager_user_id || ''} onChange={(event) => setForm({ ...form, manager_user_id: event.target.value })}>
                  <option value="">Não definir</option>
                  {userOptions.map((option) => <option key={option.value} value={option.value}>{option.label}</option>)}
                </select>
              </label>
            </div>
            <div className="form-row triple">
              <label>
                Treinador
                <select value={form.coach_user_id || ''} onChange={(event) => setForm({ ...form, coach_user_id: event.target.value })}>
                  <option value="">Não definir</option>
                  {userOptions.map((option) => <option key={option.value} value={option.value}>{option.label}</option>)}
                </select>
              </label>
              <label>
                Auxiliar
                <select value={form.assistant_user_id || ''} onChange={(event) => setForm({ ...form, assistant_user_id: event.target.value })}>
                  <option value="">Não definir</option>
                  {userOptions.map((option) => <option key={option.value} value={option.value}>{option.label}</option>)}
                </select>
              </label>
              <label>
                Analista
                <select value={form.analyst_user_id || ''} onChange={(event) => setForm({ ...form, analyst_user_id: event.target.value })}>
                  <option value="">Não definir</option>
                  {userOptions.map((option) => <option key={option.value} value={option.value}>{option.label}</option>)}
                </select>
              </label>
            </div>
            <label>
              Loja
              <select value={form.store_user_id || ''} onChange={(event) => setForm({ ...form, store_user_id: event.target.value })}>
                <option value="">Não definir</option>
                {userOptions.map((option) => <option key={option.value} value={option.value}>{option.label}</option>)}
              </select>
            </label>
            {canOverrideResponsibility && isEditing ? (
              <div className="callout-box compact-box">
                <span className="eyebrow">Saída do responsável</span>
                <strong>Resolver desistência do time</strong>
                <p>Fluxo normal: o responsável gera um código de transferência e o novo aceita. Se houver campeonato, loja, pedido ou Mercado Pago, dono/admin resolve aqui com auditoria.</p>
                <label>
                  Motivo da resolução
                  <textarea
                    value={responsibilityReason}
                    onChange={(event) => setResponsibilityReason(event.target.value)}
                    rows="3"
                    placeholder="Ex.: responsável desistiu, novo responsável autorizado pelo organizador."
                  />
                </label>
                <button
                  className="ghost-button"
                  type="button"
                  disabled={transferringResponsibility}
                  onClick={() => {
                    onTransferTeamResponsibility?.(editingId, {
                      owner_user_id: form.owner_user_id ? Number(form.owner_user_id) : null,
                      reason: responsibilityReason,
                    }).then?.(() => {
                      setResponsibilityReason('');
                      resetForm();
                    });
                  }}
                >
                  {transferringResponsibility ? 'Resolvendo...' : 'Resolver saída do responsável'}
                </button>
              </div>
            ) : null}
            <button className="primary-button" type="submit" disabled={creating || updating}>
              {isEditing ? (updating ? 'Salvando...' : 'Atualizar time') : (creating ? 'Criando...' : 'Criar time')}
            </button>
          </form>
        ) : (
          <div className="empty-state">
            {canManage ? 'Seu perfil pode editar os times vinculados a você, mas não criar novos clubes.' : 'Seu perfil pode ver os times, mas não pode alterá-los.'}
          </div>
        )}
      </SectionCard>

      <SectionCard title="Lista de times" subtitle="A tabela já mostra dono do time e comissão técnica vinculada.">
        <DataTable
          rows={data?.items || []}
          columns={[
            { key: 'name', label: 'Time' },
            { key: 'short_name', label: 'Sigla' },
            { key: 'owner_name', label: 'Responsável' },
            {
              key: 'staff',
              label: 'Comissão',
              render: (row) => row.staff?.map((item) => `${item.role}: ${item.name}`).join(' | ') || '-',
            },
            { key: 'players_count', label: 'Jogadores' },
            { key: 'status', label: 'Status', type: 'status' },
            {
              key: 'actions',
              label: 'Ações',
              render: (row) => (
                <div className="inline-actions">
                  {canManage ? (
                    <>
                      <button
                        className="inline-button"
                        type="button"
                        onClick={() => {
                          const staff = {};
                          (row.staff || []).forEach((member) => {
                            if (member.role === 'manager') staff.manager_user_id = String(member.user_id || '');
                            if (member.role === 'coach') staff.coach_user_id = String(member.user_id || '');
                            if (member.role === 'assistant') staff.assistant_user_id = String(member.user_id || '');
                            if (member.role === 'analyst') staff.analyst_user_id = String(member.user_id || '');
                            if (member.role === 'store') staff.store_user_id = String(member.user_id || '');
                          });

                          setEditingId(row.id);
                          setSelectedLogoFile(null);
                          setLogoMessage('');
                          setResponsibilityReason('');
                          setForm({
                            name: row.name || '',
                            short_name: row.short_name || '',
                            city: row.city || '',
                            state: row.state || '',
                            logo_url: row.logo_url || '',
                            primary_color: row.primary_color || '#0E7A3E',
                            secondary_color: row.secondary_color || '#D4A017',
                            owner_user_id: row.owner_user_id ? String(row.owner_user_id) : '',
                            manager_user_id: staff.manager_user_id || '',
                            coach_user_id: staff.coach_user_id || '',
                            assistant_user_id: staff.assistant_user_id || '',
                            analyst_user_id: staff.analyst_user_id || '',
                            store_user_id: staff.store_user_id || '',
                            status: row.status || 'active',
                          });
                        }}
                      >
                        Editar
                      </button>
                      <button className="inline-button danger-text" type="button" onClick={() => onDeleteTeam(row.id)} disabled={deleting}>
                        Remover
                      </button>
                    </>
                  ) : (
                    <span className="table-muted">Somente leitura</span>
                  )}
                </div>
              ),
            },
          ]}
        />
      </SectionCard>
    </div>
  );
}
